/**
 * Kontroll's overall band (#275, V7.5.6): Trenger vurdering, Klare til godkjenning and Godkjent «x av y» for
 * the scope the toolbar sets (period, team, person, search, project). The list's status, exception, sort and
 * page never change these figures. The page draws no band while loading or when the scope has no records
 * (D045, D046). Choosing a figure opens that status and clears the exception filter. The caption under it:
 * what remains, or that everything is approved, with who is at work now, and «Alle registreringer (n)».
 * Planned shifts that ended with no registration are a separate line under it (AC-13): not records, never
 * in the three figures, and the link opens exactly them.
 */
import { T } from '@/i18n';
import { num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import type { ReactNode } from 'react';
import type { Summary } from './kontroll';

export type SumKey = 'handling' | 'klare' | 'godkjent';

export function KontrollSummary({
  sum,
  selected,
  onPick,
  onRemaining,
  onAll,
  onOngoing,
  onGaps,
  gapsSelected = false,
  allSelected = false,
  allCount,
}: {
  /** null: not known (a source failed) */
  sum: Summary | null;
  selected: string | null;
  onPick: (k: SumKey) => void;
  onRemaining: () => void;
  onAll: () => void;
  onOngoing: () => void;
  onGaps: () => void;
  /** what «Alle registreringer» lists: the buckets, ongoing, and gaps (Oversikt's lists open there) */
  allCount?: number;
  /** the gap list is the list shown */
  gapsSelected?: boolean;
  /** «Alle registreringer» is the list shown */
  allSelected?: boolean;
}) {
  const v = (n: number | undefined) => (sum == null || n == null ? '–' : String(n));
  const item = (
    k: SumKey,
    icon: 'warn' | 'clock' | 'check',
    label: string,
    value: ReactNode,
    detail: string,
  ) => (
    <button
      type="button"
      className={`ksi ${k}`}
      aria-pressed={selected === k}
      disabled={sum == null}
      onClick={() => onPick(k)}
      data-testid={`sum-${k}`}
    >
      <span className="ksl">
        <Icon name={icon} size={16} />
        {label}
      </span>
      <span className="ksv">{value}</span>
      <span className="ksd">{detail}</span>
    </button>
  );
  const details = sum
    ? [
        T('{n} med uavklart arbeidstid', { n: sum.unknown }),
        sum.waiting ? T('{n} venter på ansatt', { n: sum.waiting }) : '',
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  const pending = sum ? sum.review + sum.ready : 0;
  return (
    <section className="ksum" aria-label={T('Status for valgt periode')} data-testid="kontroll-summary">
      <div className="ksumb">
        {item('handling', 'warn', T('Trenger vurdering'), v(sum?.review), details)}
        {item('klare', 'clock', T('Klare til godkjenning'), v(sum?.ready), T('Gjennomgå og godkjenn samlet'))}
        {item(
          'godkjent',
          'check',
          T('Godkjent'),
          <>
            {v(sum?.approved)}
            <small>{T('av {n}', { n: v(sum?.total) })}</small>
          </>,
          sum ? T('{h} t godkjent arbeidstid', { h: num(sum.approvedHours) }) : '',
        )}
      </div>
      {sum && (
        <div className="ksumf">
          <span>
            {pending > 0 ? (
              <button type="button" className="mlink" onClick={onRemaining}>
                {T('{n, plural, one {# registrering gjenstår} other {# registreringer gjenstår}}', {
                  n: pending,
                })}
              </button>
            ) : sum.total > 0 ? (
              T('{n, plural, one {# registrering er godkjent} other {# registreringer er godkjent}}', {
                n: sum.total,
              })
            ) : (
              T('Ingen avsluttede registreringer')
            )}
            {sum.ongoing > 0 && (
              <>
                {', '}
                <button type="button" className="mlink" onClick={onOngoing}>
                  {T('{n} pågår', { n: sum.ongoing })}
                </button>
              </>
            )}
          </span>
          <button
            type="button"
            className={`mlink${allSelected ? ' on' : ''}`}
            aria-pressed={allSelected}
            onClick={onAll}
          >
            {T('Alle registreringer ({n})', { n: allCount ?? sum.total + sum.ongoing })}
          </button>
        </div>
      )}
      {sum && (sum.gaps > 0 || sum.accounted > 0) && (
        <p className="kgaps" data-testid="kontroll-gaps">
          {sum.gaps > 0 && (
            <>
              <Icon name="warn" size={16} />
              <span>
                {T(
                  '{n, plural, one {# planlagt vakt er avsluttet uten registrering.} other {# planlagte vakter er avsluttet uten registrering.}}',
                  { n: sum.gaps },
                )}{' '}
                {T('De telles ikke i tallene over.')}
              </span>
              <button
                type="button"
                className={`mlink${gapsSelected ? ' on' : ''}`}
                aria-pressed={gapsSelected}
                onClick={onGaps}
              >
                {T('Vis vaktene')}
              </button>
            </>
          )}
          {sum.accounted > 0 && (
            <span className="kgapsok">
              {T(
                '{n, plural, one {# planlagt vakt uten registrering er dekket av godkjent fravær.} other {# planlagte vakter uten registrering er dekket av godkjent fravær.}}',
                { n: sum.accounted },
              )}
            </span>
          )}
        </p>
      )}
    </section>
  );
}
